import type { LucideIcon } from 'lucide-react';
import type { AdminFeatureFlagKey } from '@/config/admin-feature-flags';
import type { AdminBadgeKey } from '@/lib/admin-badges';
import type { PermissionRequirement } from '@/lib/permissions';
import {
  Activity,
  BarChart3,
  Bell,
  Blocks,
  Bot,
  Building2,
  CalendarDays,
  CircleDollarSign,
  ClipboardCheck,
  Clock3,
  Cog,
  CreditCard,
  FileSearch,
  Flag,
  Gauge,
  Globe,
  LayoutDashboard,
  Layers,
  Mail,
  Megaphone,
  MessageSquare,
  Receipt,
  Rocket,
  ScanEye,
  SearchCheck,
  Settings,
  Share2,
  Shield,
  ShieldAlert,
  ShieldCheck,
  SlidersHorizontal,
  Sparkles,
  Database,
  HardDrive,
  Search,
  ListOrdered,
  AlertCircle,
  Code2,
  Tags,
  Terminal,
  Zap,
  UserCheck,
  UserCog,
  UserRoundX,
  Users,
  Wallet,
  Waypoints,
} from 'lucide-react';
import { buildAdminNavSections } from './admin-nav-sections.prod';

export const ADMIN_TAB_KEYS = {
  OVERVIEW: 'overview',
  GLOBAL_ANALYTICS: 'global-analytics',
  LIVE_METRICS: 'live-metrics',
  ACTIVITY_FEED: 'activity-feed',
  OPERATIONS_DASHBOARD: 'operations-dashboard',
  ADMIN_NOTIFICATIONS: 'admin-notifications',
  EVENTS_AUDIT: 'events',
  EVENT_APPROVALS: 'event-approvals',
  EVENT_GOVERNANCE: 'event-governance',
  USER_GOVERNANCE: 'user-governance',
  TEAM_ASSIGNMENTS: 'team-assignments',
  TEAM_ESCALATIONS: 'team-escalations',
  OPERATIONS_CENTER: 'operations-center',
  CASE_MANAGEMENT: 'case-management',
  CONVERSATION_SUPERVISION: 'conversation-supervision',
  FEATURED_EVENTS: 'featured-events',
  REPORTS_VIOLATIONS: 'violations',
  CATEGORIES_AND_TAGS: 'categories-tags',
  EVENT_FRAMEWORKS: 'event-frameworks',
  EVENT_TEMPLATES: 'event-templates',
  SUBSCRIPTION_MATRIX: 'subscription-matrix',
  CUSTOM_PLAN_REQUESTS: 'custom-plan-requests',
  USERS: 'users',
  ADMIN_ACCOUNTS: 'admin-accounts',
  ADMIN_TEAMS: 'admin-teams',
  ADMIN_RBAC_AUDIT: 'admin-rbac-audit',
  ORGANIZERS: 'organizers',
  INDIVIDUAL_ORGANIZERS: 'individual-organizers',
  JURY_MEMBERS: 'jury',
  TEAMS: 'teams',
  COLLEGES: 'colleges',
  ROLES_PERMISSIONS: 'roles',
  PERMISSION_REQUESTS: 'permission-requests',
  APPROVAL_QUEUE: 'approval-queue',
  TEMPORARY_ACCESS: 'temporary-access',
  ACCESS_AUDIT_LOGS: 'access-audit-logs',
  HIERARCHY_MANAGEMENT: 'hierarchy-management',
  VERIFICATION_REQUESTS: 'verifications',
  KYC_MANAGEMENT: 'kyc-management',
  IDENTITY_VERIFICATIONS: 'identity-verifications',
  APPROVALS: 'approvals',
  ORGANIZER_APPROVALS: 'organizer-approvals',
  ORGANIZER_PENDING_QUEUE: 'organizer-pending-queue',
  SETTLEMENT_ACCOUNTS: 'settlement-accounts',
  SETTLEMENT_QUEUE: 'settlement-queue',
  FINANCE_OPERATIONS: 'finance-operations',
  SUSPENDED_ACCOUNTS: 'suspended',
  PAYOUTS: 'payouts',
  SETTLEMENTS: 'settlements',
  TRANSACTIONS: 'transactions',
  REVENUE_ANALYTICS: 'revenue-analytics',
  COMMISSION_SETTINGS: 'commission-settings',
  REFUNDS: 'refunds',
  COUPONS: 'coupons',
  SUBSCRIPTION_PLANS: 'subscription-plans',
  FEATURE_BUNDLES: 'feature-bundles',
  USER_SUBSCRIPTIONS: 'user-subscriptions',
  MESSAGE_INBOX: 'messages',
  SUPPORT_INBOX: 'support',
  ANNOUNCEMENTS: 'announcements',
  NOTIFICATION_BROADCASTS: 'broadcasts',
  NOTIFICATION_DELIVERY: 'notification-delivery',
  PLATFORM_AUDIT: 'platform-audit',
  AUDIT_COVERAGE: 'audit-coverage',
  NAVIGATION_HEALTH: 'navigation-health',
  WORKFLOW_CERTIFICATION: 'workflow-certification',
  WORKFLOW_ANALYTICS: 'workflow-analytics',
  LAUNCH_READINESS: 'launch-readiness',
  CRUD_CERTIFICATION: 'crud-certification',
  EMAIL_CONFIGURATION: 'email-configuration',
  EMAIL_INFRASTRUCTURE: 'email-infrastructure',
  EMAIL_HEALTH: 'email-health',
  EMAIL_CAMPAIGNS: 'email-campaigns',
  PUSH_NOTIFICATIONS: 'push-notifications',
  PLATFORM_RESOURCES: 'resources',
  TESTIMONIALS: 'testimonials',
  SOCIAL_LINKS: 'social-links',
  BLOGS: 'blogs',
  BANNERS: 'banners',
  HOMEPAGE_CMS: 'homepage-cms',
  FAQS: 'faqs',
  PARTNERS_DIRECTORY: 'partners-directory',
  SPONSORS: 'sponsors',
  AUDIT_LOGS: 'audit-logs',
  SECURITY_LOGS: 'security-logs',
  ADMIN_ACTIVITY: 'admin-activity',
  API_MONITORING: 'api-monitoring',
  PLATFORM_HEALTH: 'platform-health',
  PLATFORM_PERFORMANCE: 'platform-performance',
  SECURITY_CENTER: 'security-center',
  MARKETPLACE_GOVERNANCE: 'marketplace',
  RELEASE_OPS: 'release-ops',
  ACCESS_CONTROL: 'access-control',
  PROFILE: 'profile',
  SETUP_2FA: 'setup-2fa',
  SESSION_MONITORING: 'session-monitoring',
  IP_RESTRICTIONS: 'ip-restrictions',
  AUDIT_INTEGRITY: 'audit-integrity',
  RETENTION_POLICIES: 'retention-policies',
  FRAUD_DETECTION: 'fraud-detection',
  COMPLIANCE_REPORTS: 'compliance-reports',
  PLATFORM_SETTINGS: 'settings',
  MAINTENANCE_MODE: 'maintenance-mode',
  BRANDING: 'branding',
  INTEGRATIONS: 'integrations',
  PAYMENT_GATEWAYS: 'payment-gateways',
  PLATFORM_TAX_SETTINGS: 'platform-tax-settings',
  SMTP_SETTINGS: 'smtp',
  STORAGE_SETTINGS: 'storage',
  FEATURE_FLAGS: 'feature-flags',
  ENVIRONMENT_CONTROLS: 'platform-readiness',
  AI_MODERATION: 'ai-moderation',
  SMART_RECOMMENDATIONS: 'smart-recommendations',
  FRAUD_INTELLIGENCE: 'fraud-intelligence',
  AI_INSIGHTS: 'ai-insights',
  AUTO_CATEGORIZATION: 'auto-categorization',
  RESOURCE_ARTICLES: 'resource-articles',
  RESOURCE_VIDEOS: 'resource-videos',
  RESOURCE_SUCCESS_STORIES: 'resource-success-stories',
  RESOURCE_FAQS: 'resource-faqs',
  RESOURCE_USER_QUESTIONS: 'resource-user-questions',
  ORGANIZER_GUIDE: 'organizer-guide',
  EVENT_DISCOVERY_CATEGORIES: 'event-discovery-categories',
  EVENT_DISCOVERY_TEMPLATES: 'event-discovery-templates',
  EVENT_DISCOVERY_FEATURED_SECTIONS: 'event-discovery-featured-sections',
  EVENT_DISCOVERY_ANALYTICS: 'event-discovery-analytics',
  PRIVACY_POLICY: 'privacy-policy',
  CONSENT_RECORDS: 'consent-records',
  /** Dedicated Next.js routes (slug matches path segment under /dashboard/admin/). */
  LEGAL_DOCUMENTS: 'legal-documents',
  DAVS: 'davs',
  QUEUES: 'queues',
  SCHEDULER: 'scheduler',
  LOG_EXPLORER: 'logs',
  CACHE_MANAGER: 'cache',
  BACKUP_CENTER: 'backups',
  SEARCH_INDEX: 'search-index',
  NOTIFICATION_POLICIES: 'notification-policies',
  COMMUNICATION_SENDERS: 'communication-senders',
  MUN_COUNCIL_TYPES: 'mun-council-types',
} as const;

/** Canonical hrefs for admin modules implemented as dedicated page.tsx routes. */
export const ADMIN_DEDICATED_ROUTES = {
  LEGAL_CENTER: '/dashboard/admin/legal-documents',
  DAVS: '/dashboard/admin/davs',
  DAVS_DOCUMENTS: '/dashboard/admin/davs/documents',
  DAVS_DRAFTS: '/dashboard/admin/davs/drafts',
  DAVS_ISSUE: '/dashboard/admin/davs/documents/issue',
  DAVS_APPROVALS: '/dashboard/admin/davs/approvals',
  DAVS_UPLOAD: '/dashboard/admin/davs/documents/upload',
  DAVS_TEMPLATES: '/dashboard/admin/davs/templates',
  DAVS_LETTERHEADS: '/dashboard/admin/davs/letterheads',
  DAVS_QR_STUDIO: '/dashboard/admin/davs/qr-studio',
  DAVS_DOCUMENT_TYPES: '/dashboard/admin/davs/document-types',
  DAVS_VERIFICATION: '/dashboard/admin/davs/verification',
  DAVS_AUDIT: '/dashboard/admin/davs/audit',
  DAVS_SETTINGS: '/dashboard/admin/davs/settings',
  QUEUE_MONITOR: '/dashboard/admin/queues',
  SCHEDULER: '/dashboard/admin/scheduler',
  LOG_EXPLORER: '/dashboard/admin/logs',
  CACHE_MANAGER: '/dashboard/admin/cache',
  BACKUP_CENTER: '/dashboard/admin/backups',
  SEARCH_INDEX: '/dashboard/admin/search-index',
  NOTIFICATION_POLICIES: '/dashboard/admin/notification-policies',
  COMMUNICATION_SENDERS: '/dashboard/admin/communication-senders',
  OPERATIONS_HUB: '/dashboard/admin/operations',
  MARKETPLACE: '/dashboard/admin/marketplace',
  RELEASE_OPS: '/dashboard/admin/release-ops',
} as const;

/** Set of admin paths that have dedicated page.tsx routes under src/app/(system)/dashboard/admin. */
export const ADMIN_PHYSICAL_ROUTES = new Set<string>([
  '/dashboard/admin',
  ...Object.values(ADMIN_DEDICATED_ROUTES),
  '/dashboard/admin/platform-health',
  '/dashboard/admin/platform-performance',
  '/dashboard/admin/feature-flags',
  '/dashboard/admin/notifications-ops',
  '/dashboard/admin/finance',
  '/dashboard/admin/settlements',
  '/dashboard/admin/security-center',
  '/dashboard/admin/setup-2fa',
  '/dashboard/admin/change-password',
  '/dashboard/admin/audit',
]);

/** Returns true if the admin route has a dedicated physical page.tsx on disk. */
export function hasDedicatedAdminRoute(href: string | null | undefined): boolean {
  if (!href) return false;
  const cleanPath = href.split('?')[0].replace(/\/$/, '') || '/dashboard/admin';
  return ADMIN_PHYSICAL_ROUTES.has(cleanPath);
}

export type AdminTabKey = (typeof ADMIN_TAB_KEYS)[keyof typeof ADMIN_TAB_KEYS];


import type { AdminNavItem as GenericAdminNavItem, AdminNavSection as GenericAdminNavSection, AdminNavStatus } from './admin-navigation-types';
export type { AdminNavStatus };
export type AdminNavItem = GenericAdminNavItem<AdminTabKey>;
export type AdminNavSection = GenericAdminNavSection<AdminTabKey>;

export const DEFAULT_ADMIN_TAB: AdminTabKey = ADMIN_TAB_KEYS.OVERVIEW;

/**
 * Returns the effective status of an admin nav item.
 * Defaults to 'planned' if unspecified, guaranteeing safe fallback with zero prefetching.
 */
export function getAdminNavItemStatus(item: { status?: AdminNavStatus } | null | undefined): AdminNavStatus {
  return 'live';
}

export const ADMIN_NAV_SECTIONS: AdminNavSection[] = buildAdminNavSections(
  ADMIN_TAB_KEYS,
  ADMIN_DEDICATED_ROUTES,
) as unknown as AdminNavSection[];

export const ADMIN_TAB_VALUES = ADMIN_NAV_SECTIONS.flatMap((section) =>
  section.items.map((item) => item.tab)
) as AdminTabKey[];

export function isAdminTabKey(value: string | null | undefined): value is AdminTabKey {
  if (!value) return false;
  return ADMIN_TAB_VALUES.includes(value as AdminTabKey);
}

export function normalizeAdminTab(value: string | null | undefined): AdminTabKey {
  return isAdminTabKey(value) ? value : DEFAULT_ADMIN_TAB;
}

const LEGACY_ADMIN_TAB_ALIASES: Record<string, AdminTabKey> = {
  dashboard: ADMIN_TAB_KEYS.OVERVIEW,
  overview: ADMIN_TAB_KEYS.OVERVIEW,
  events: ADMIN_TAB_KEYS.EVENTS_AUDIT,
  'events-audit': ADMIN_TAB_KEYS.EVENTS_AUDIT,
  messages: ADMIN_TAB_KEYS.MESSAGE_INBOX,
  support: ADMIN_TAB_KEYS.SUPPORT_INBOX,
  settings: ADMIN_TAB_KEYS.PLATFORM_SETTINGS,
  resources: ADMIN_TAB_KEYS.PLATFORM_RESOURCES,
  'organizer-guide': ADMIN_TAB_KEYS.ORGANIZER_GUIDE,
  organizers: ADMIN_TAB_KEYS.ORGANIZERS,
  users: ADMIN_TAB_KEYS.USERS,
  'customer-users': ADMIN_TAB_KEYS.USERS,
  'admin-accounts': ADMIN_TAB_KEYS.ADMIN_ACCOUNTS,
  'admin-teams': ADMIN_TAB_KEYS.ADMIN_TEAMS,
  'team-assignments': ADMIN_TAB_KEYS.TEAM_ASSIGNMENTS,
  'team-escalations': ADMIN_TAB_KEYS.TEAM_ESCALATIONS,
  'operations-center': ADMIN_TAB_KEYS.OPERATIONS_CENTER,
  'case-management': ADMIN_TAB_KEYS.CASE_MANAGEMENT,
  'conversation-supervision': ADMIN_TAB_KEYS.CONVERSATION_SUPERVISION,
  'admin-rbac-audit': ADMIN_TAB_KEYS.ADMIN_RBAC_AUDIT,
  roles: ADMIN_TAB_KEYS.ROLES_PERMISSIONS,
  testimonials: ADMIN_TAB_KEYS.TESTIMONIALS,
  'social-links': ADMIN_TAB_KEYS.SOCIAL_LINKS,
  settlements: ADMIN_TAB_KEYS.SETTLEMENTS,
  payouts: ADMIN_TAB_KEYS.PAYOUTS,
  'subscription-plans': ADMIN_TAB_KEYS.SUBSCRIPTION_PLANS,
  'feature-bundles': ADMIN_TAB_KEYS.FEATURE_BUNDLES,
  'user-subscriptions': ADMIN_TAB_KEYS.USER_SUBSCRIPTIONS,
  'admin-activity': ADMIN_TAB_KEYS.ADMIN_ACTIVITY,
  'resource-articles': ADMIN_TAB_KEYS.RESOURCE_ARTICLES,
  'resource-videos': ADMIN_TAB_KEYS.RESOURCE_VIDEOS,
  'resource-success-stories': ADMIN_TAB_KEYS.RESOURCE_SUCCESS_STORIES,
  'resource-faqs': ADMIN_TAB_KEYS.RESOURCE_FAQS,
  'resource-user-questions': ADMIN_TAB_KEYS.RESOURCE_USER_QUESTIONS,
  categories: ADMIN_TAB_KEYS.CATEGORIES_AND_TAGS,
  tags: ADMIN_TAB_KEYS.CATEGORIES_AND_TAGS,
  venues: ADMIN_TAB_KEYS.OVERVIEW,
  'event-discovery-categories': ADMIN_TAB_KEYS.EVENT_DISCOVERY_CATEGORIES,
  'event-discovery-templates': ADMIN_TAB_KEYS.EVENT_DISCOVERY_TEMPLATES,
  'event-discovery-featured-sections': ADMIN_TAB_KEYS.EVENT_DISCOVERY_FEATURED_SECTIONS,
  'event-discovery-analytics': ADMIN_TAB_KEYS.EVENT_DISCOVERY_ANALYTICS,
  'privacy-policy': ADMIN_TAB_KEYS.PRIVACY_POLICY,
  'consent-records': ADMIN_TAB_KEYS.CONSENT_RECORDS,
  profile: ADMIN_TAB_KEYS.PROFILE,
  'setup-2fa': ADMIN_TAB_KEYS.SETUP_2FA,
  'platform-health': ADMIN_TAB_KEYS.PLATFORM_HEALTH,
  'platform-audit': ADMIN_TAB_KEYS.PLATFORM_AUDIT,
  'audit-coverage': ADMIN_TAB_KEYS.AUDIT_COVERAGE,
  'navigation-health': ADMIN_TAB_KEYS.NAVIGATION_HEALTH,
  'workflow-certification': ADMIN_TAB_KEYS.WORKFLOW_CERTIFICATION,
  'launch-readiness': ADMIN_TAB_KEYS.LAUNCH_READINESS,
  'email-configuration': ADMIN_TAB_KEYS.EMAIL_CONFIGURATION,
  'email-infrastructure': ADMIN_TAB_KEYS.EMAIL_CONFIGURATION,
  'email-health': ADMIN_TAB_KEYS.EMAIL_HEALTH,
  'platform-performance': ADMIN_TAB_KEYS.PLATFORM_PERFORMANCE,
  'environment-controls': ADMIN_TAB_KEYS.ENVIRONMENT_CONTROLS,
  'platform-readiness': ADMIN_TAB_KEYS.ENVIRONMENT_CONTROLS,
  'security-center': ADMIN_TAB_KEYS.SECURITY_CENTER,
  marketplace: ADMIN_TAB_KEYS.MARKETPLACE_GOVERNANCE,
  'release-ops': ADMIN_TAB_KEYS.RELEASE_OPS,
  'api-monitoring': ADMIN_TAB_KEYS.PLATFORM_HEALTH,
  'live-metrics': ADMIN_TAB_KEYS.PLATFORM_HEALTH,
  'live-platform-metrics': ADMIN_TAB_KEYS.PLATFORM_HEALTH,
  'commission-settings': ADMIN_TAB_KEYS.SUBSCRIPTION_PLANS,
  'legal-documents': ADMIN_TAB_KEYS.LEGAL_DOCUMENTS,
  davs: ADMIN_TAB_KEYS.DAVS,
  queues: ADMIN_TAB_KEYS.QUEUES,
  scheduler: ADMIN_TAB_KEYS.SCHEDULER,
  logs: ADMIN_TAB_KEYS.LOG_EXPLORER,
  cache: ADMIN_TAB_KEYS.CACHE_MANAGER,
  backups: ADMIN_TAB_KEYS.BACKUP_CENTER,
  'search-index': ADMIN_TAB_KEYS.SEARCH_INDEX,
  'notification-policies': ADMIN_TAB_KEYS.NOTIFICATION_POLICIES,
  'communication-senders': ADMIN_TAB_KEYS.COMMUNICATION_SENDERS,
  'crud-certification': ADMIN_TAB_KEYS.AUDIT_COVERAGE,
  'subscriptions': ADMIN_TAB_KEYS.SUBSCRIPTION_PLANS,
  'custom-plans': ADMIN_TAB_KEYS.SUBSCRIPTION_PLANS,
  'custom-plan-requests': ADMIN_TAB_KEYS.CUSTOM_PLAN_REQUESTS,
  'verification-requests': ADMIN_TAB_KEYS.VERIFICATION_REQUESTS,
  'organization-verifications': ADMIN_TAB_KEYS.VERIFICATION_REQUESTS,
  'verifications': ADMIN_TAB_KEYS.VERIFICATION_REQUESTS,
  'registrations': ADMIN_TAB_KEYS.VERIFICATION_REQUESTS,
  approvals: ADMIN_TAB_KEYS.APPROVALS,
  'mun-council-types': ADMIN_TAB_KEYS.MUN_COUNCIL_TYPES,
};

export function normalizeAdminTabOrAlias(value: string | null | undefined): AdminTabKey {
  if (!value) return DEFAULT_ADMIN_TAB;
  const normalized = String(value).trim().toLowerCase();
  if (isAdminTabKey(normalized)) return normalized;
  return LEGACY_ADMIN_TAB_ALIASES[normalized] ?? DEFAULT_ADMIN_TAB;
}

export function tabFromAdminPath(pathname: string | null | undefined): AdminTabKey {
  if (!pathname) return DEFAULT_ADMIN_TAB;
  const parts = pathname.split('/').filter(Boolean);
  const isLegacy = parts[0] === 'admin';
  const isDashboardAdmin = parts[0] === 'dashboard' && parts[1] === 'admin';
  if (!isLegacy && !isDashboardAdmin) return DEFAULT_ADMIN_TAB;
  const segment = isDashboardAdmin ? (parts[2] ?? 'dashboard') : (parts[1] ?? 'dashboard');
  if (parts.length > (isDashboardAdmin ? 3 : 2)) {
    const subSegment = isDashboardAdmin ? parts[3] : parts[2];
    if (subSegment === 'custom-plans') {
      return ADMIN_TAB_KEYS.SUBSCRIPTION_PLANS;
    }
  }
  return normalizeAdminTabOrAlias(segment);
}

export function buildAdminTabHref(tab: AdminTabKey): string {
  return tab === ADMIN_TAB_KEYS.OVERVIEW ? '/dashboard/admin' : `/dashboard/admin/${tab}`;
}

export function buildAdminNavItemHref(item: AdminNavItem): string {
  return item.href ?? buildAdminTabHref(item.tab);
}

/** Static export catch-all segments for /dashboard/admin/[...slug] and legacy /admin/[...slug]. */
export function getAdminCatchAllStaticParams(): { slug: string[] }[] {
  const slugs = new Set<string>(Object.values(ADMIN_TAB_KEYS));
  slugs.add('dashboard');
  const params: { slug: string[] }[] = [...slugs].map((segment) => ({ slug: [segment] }));
  params.push({ slug: ['subscriptions', 'custom-plans'] });
  params.push({ slug: ['subscription-plans', 'custom-plans'] });
  params.push({ slug: ['verification-requests'] });
  params.push({ slug: ['organization-verifications'] });
  params.push({ slug: ['approvals'] });
  return params;
}

export function buildAdminTabHrefWithQuery(
  tab: AdminTabKey,
  query?: Record<string, string | number | null | undefined>,
): string {
  const base = buildAdminTabHref(tab);
  if (!query) return base;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== null && value !== undefined && String(value) !== '') {
      params.set(key, String(value));
    }
  }
  const qs = params.toString();
  return qs ? `${base}?${qs}` : base;
}

/** Dashboard and notification deep-link targets (single source of truth). */
export const ADMIN_DASHBOARD_LINKS = {
  activeEvents: buildAdminTabHref(ADMIN_TAB_KEYS.EVENTS_AUDIT),
  partners: buildAdminTabHref(ADMIN_TAB_KEYS.PARTNERS_DIRECTORY),
  pendingSignups: buildAdminTabHrefWithQuery(ADMIN_TAB_KEYS.VERIFICATION_REQUESTS, { status: 'pending' }),
  testimonials: buildAdminTabHrefWithQuery(ADMIN_TAB_KEYS.TESTIMONIALS, { status: 'pending' }),
  inbox: buildAdminTabHref(ADMIN_TAB_KEYS.MESSAGE_INBOX),
  pendingApprovals: buildAdminTabHrefWithQuery(ADMIN_TAB_KEYS.VERIFICATION_REQUESTS, { status: 'pending' }),
  supportCases: buildAdminTabHref(ADMIN_TAB_KEYS.SUPPORT_INBOX),
  pendingPayouts: buildAdminTabHref(ADMIN_TAB_KEYS.PAYOUTS),
  unreadMessages: buildAdminTabHref(ADMIN_TAB_KEYS.MESSAGE_INBOX),
  platformHealth: buildAdminTabHref(ADMIN_TAB_KEYS.PLATFORM_HEALTH),
  auditLogs: buildAdminTabHref(ADMIN_TAB_KEYS.AUDIT_LOGS),
  notifications: buildAdminTabHref(ADMIN_TAB_KEYS.ADMIN_NOTIFICATIONS),
  eventApprovals: buildAdminTabHrefWithQuery(ADMIN_TAB_KEYS.EVENT_APPROVALS, { status: 'pending_review' }),
  organizations: buildAdminTabHref(ADMIN_TAB_KEYS.ORGANIZERS),
} as const;

export function verificationRequestHref(id?: string): string {
  if (id) {
    return buildAdminTabHrefWithQuery(ADMIN_TAB_KEYS.VERIFICATION_REQUESTS, { focus: id });
  }
  return ADMIN_DASHBOARD_LINKS.pendingApprovals;
}

export function getAdminTabMeta(tab: AdminTabKey) {
  for (const section of ADMIN_NAV_SECTIONS) {
    const item = section.items.find((entry) => entry.tab === tab);
    if (item) {
      return {
        sectionId: section.id,
        sectionLabel: section.label,
        label: item.label,
        description: item.description,
        icon: item.icon,
      };
    }
  }

  if (tab === ADMIN_TAB_KEYS.SETUP_2FA) {
    return {
      sectionId: 'security',
      sectionLabel: 'Security',
      label: 'Set up two-factor authentication',
      description: 'Admin access requires an authenticator app before you can use the dashboard.',
      icon: ShieldCheck,
    };
  }

  return {
    sectionId: 'overview',
    sectionLabel: 'Overview',
    label: 'Dashboard',
    description: 'Administrative control center.',
    icon: LayoutDashboard,
  };
}
